import { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { Box, Spinner } from '@chakra-ui/react';

import { useHeader } from '@app/components/common/navigation/hooks/header-service';
import { useAlert, useAutocomplete } from '@app/hooks';
import { api } from '@app/api';
import { MasterOutlet } from '@app/api/models';
import { Paper } from '@app/components/common';
import { ROLES } from '@app/navigation/types';
import { usePermissions } from '@app/hooks/use-permissions';
import Error404 from '@app/pages/errors/404';

import { MasterOutletFormInstance } from './types';
import OutletForm from './form';
import { mapInstance } from './utils/mapInstance';

const OutletEdit = () => {
  useHeader('Изменить мастер ТТ', []);
  const params = useParams<{ id: string }>();

  const { state } = useLocation<{ masterOutlet?: MasterOutlet }>();
  const history = useHistory();
  const { success, error } = useAlert();
  const [_, setIsFetching] = useState<boolean>(false);
  const getAutocomplete = useAutocomplete();

  const [instance, setInstance] = useState<
    MasterOutletFormInstance | undefined
  >(undefined);

  useEffect(() => {
    void (async () => {
      if(!params.id) {
        return;
      }
      const { data } = await api.acb.getMasterOutlet(Number(params.id));
      if (data.code !== 200) {
        error(data.message);
        return;
      }
      const regions = await getAutocomplete('region', { search: data?.data.address.region });
      const cities = await getAutocomplete('city', { search: data?.data.address.city });
      const region = regions?.[0];
      const city = cities?.[0];

      let parent: EntityBase | null = null;
      if(data.data.parent_id) {
        const { data: parentData } = await api.acb.getMasterOutlet(data.data.parent_id);
        parent = parentData.data;
      }

      setInstance({
        ...state?.masterOutlet,
        id: state?.masterOutlet.id,
        price: state?.masterOutlet?.price_list,
        photos:
          data.data?.images?.map(({ image_pid }) => ({
            image_pid,
            filename: '',
            content: '',
          })) || [],
        ...data.data,
        address: {
          id: data.data?.address?.id,
          full_address: data.data?.address?.full_address,
          address: data.data?.address?.full_address,
          region,
          city,
          latlng: data.data?.address?.latlng,
        },
        parent,
      });
    })();


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, state]);

  const onFormSubmit = async (instance: Partial<MasterOutletFormInstance>) => {
    setIsFetching(true);
    try {
      const response = await api.acb.modifyMasterOutlet(mapInstance(instance));
      if (response.data.code === 200) {
        setIsFetching(false);
        success('Мастер ТТ изменена');
        history.goBack();
      } else {
        setIsFetching(false);
        error(response.data.message);
      }
    } catch (e) {
      console.log(e);
      
      setIsFetching(false);
      error(e.message);
    }
  };

  const onFormCancel = () => {
    history.goBack();
  };

  const canEdit = usePermissions({ roles: [ROLES.ADMIN, ROLES.CHIEF] });

  if (!canEdit) {
    return <Error404 />;
  }

  return (
    <Box paddingX={{ xs: 0, sm: 2 }}>
      {!instance ? (
        <Box
          width="100%"
          height="300px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Spinner />
        </Box>
      ) : (
        <Paper>
          <Box paddingX={4} paddingY={6}>
            <OutletForm
              instance={instance}
              onSubmit={onFormSubmit}
              onCancel={onFormCancel}
              submitText="Изменить"
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default OutletEdit;
